import { useState, useEffect } from "react";
import { T } from "../../theme";
import { apiCall } from "../../apiClient";
import { Btn, Inp, Sel, Card, Badge, SectionTitle } from "../primitives";

export const PromptTemplateManager = () => {
  const [prompts, setPrompts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<any>(null);
  const [form, setForm] = useState<Record<string, any>>({ name: '', description: '', prompt_text: '', category: 'Custom', is_global: false, inject_as_system: false });
  const [optimizing, setOptimizing] = useState(false);
  const [optimized, setOptimized] = useState<any>(null);

  const load = async () => { setLoading(true); const r = await apiCall('/prompts'); if (r?.prompts) setPrompts(r.prompts); setLoading(false); };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name || !form.prompt_text) return;
    if (editing && editing !== 'new') {
      await apiCall(`/prompts/${editing}`, { method: 'PUT', body: JSON.stringify(form) });
    } else {
      await apiCall('/prompts', { method: 'POST', body: JSON.stringify(form) });
    }
    setEditing(null); setForm({ name: '', description: '', prompt_text: '', category: 'Custom', is_global: false, inject_as_system: false }); setOptimized(null); load();
  };

  const optimize = async () => {
    if (!form.prompt_text) return;
    setOptimizing(true);
    const r = await apiCall('/prompts/optimize', { method: 'POST', body: JSON.stringify({ prompt: form.prompt_text }) });
    setOptimizing(false);
    if (r?.optimized) setOptimized(r);
  };

  const CATEGORIES = ['Review', 'Drafting', 'Analysis', 'Extraction', 'Custom'];

  if (loading) return <div style={{ textAlign: "center", padding: 20, color: T.txM, fontSize: 12 }}>Loading...</div>;

  return (
    <div>
      <SectionTitle sub="Reusable AI prompt templates">Prompt Templates</SectionTitle>
      {!editing && prompts.map(p => (
        <Card key={p.id} style={{ marginBottom: 6 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: T.tx }}>{p.name}</div>
              <div style={{ fontSize: 11, color: T.txD }}>{p.description || p.prompt_text?.substring(0, 80) + '...'}</div>
            </div>
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <Badge c={T.acc} b={T.accS}>{p.category}</Badge>
              {p.is_global && <Badge c={T.prp} b={T.prpS}>Global</Badge>}
              <Btn v="secondary" sz="sm" onClick={() => { setEditing(p.id); setForm({ name: p.name, description: p.description || '', prompt_text: p.prompt_text, category: p.category, is_global: p.is_global, inject_as_system: p.inject_as_system || false }); }}>Edit</Btn>
              <button onClick={async () => { if (!window.confirm('Delete this template?')) return; await apiCall(`/prompts/${p.id}`, { method: 'DELETE' }); load(); }} style={{ fontSize: 10, color: T.red, background: "none", border: "none", cursor: "pointer", fontWeight: 600 }}>Delete</button>
            </div>
          </div>
        </Card>
      ))}
      {!editing && <Btn v="primary" sz="sm" onClick={() => setEditing('new')} style={{ marginTop: 8 }}>+ Add Prompt Template</Btn>}
      {editing && (
        <Card style={{ marginTop: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.tx, marginBottom: 12 }}>{editing === 'new' ? 'New Prompt' : 'Edit Prompt'}</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
            <Inp label="Name" value={form.name} onChange={v => setForm(f => ({ ...f, name: v }))} />
            <Sel label="Category" value={form.category} onChange={v => setForm(f => ({ ...f, category: v }))} options={CATEGORIES.map(c => ({ value: c, label: c }))} />
          </div>
          <Inp label="Description" value={form.description} onChange={v => setForm(f => ({ ...f, description: v }))} />
          <div style={{ marginTop: 12 }}>
            <label style={{ display: "block", fontSize: 10, fontWeight: 700, color: T.txD, marginBottom: 5, textTransform: "uppercase", letterSpacing: ".04em" }}>Prompt Text</label>
            <textarea value={form.prompt_text} onChange={e => { setForm(f => ({ ...f, prompt_text: e.target.value })); setOptimized(null); }} rows={6} style={{ width: "100%", minHeight: 120, padding: "10px", borderRadius: 5, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 12, fontFamily: "inherit", resize: "vertical", boxSizing: "border-box" }} />
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 8 }}>
            <Btn v="secondary" sz="sm" onClick={() => { optimize(); }} disabled={optimizing || !form.prompt_text}>
              {optimizing ? 'Optimizing...' : 'Optimize with AI'}
            </Btn>
            <label style={{ fontSize: 11, color: T.txM, display: "flex", alignItems: "center", gap: 4 }}>
              <input type="checkbox" checked={form.is_global} onChange={e => setForm(f => ({ ...f, is_global: e.target.checked }))} /> Global
            </label>
            <label style={{ fontSize: 11, color: T.txM, display: "flex", alignItems: "center", gap: 4 }}>
              <input type="checkbox" checked={form.inject_as_system} onChange={e => setForm(f => ({ ...f, inject_as_system: e.target.checked }))} /> System Prompt
            </label>
          </div>
          {optimized && (
            <div style={{ marginTop: 12, padding: 12, border: `1px solid ${T.grn}`, borderRadius: 6, backgroundColor: T.grnS }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: T.grn, marginBottom: 6 }}>AI-Optimized Version</div>
              <div style={{ fontSize: 12, color: T.tx, whiteSpace: "pre-line", marginBottom: 8 }}>{optimized.optimized}</div>
              {optimized.improvements?.length > 0 && (
                <div style={{ fontSize: 10, color: T.txD }}>{optimized.improvements.map((imp: any, i: number) => <div key={i}>- {imp}</div>)}</div>
              )}
              <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                <Btn v="primary" sz="sm" onClick={() => { setForm(f => ({ ...f, prompt_text: optimized.optimized })); setOptimized(null); }}>Accept</Btn>
                <Btn v="secondary" sz="sm" onClick={() => setOptimized(null)}>Reject</Btn>
              </div>
            </div>
          )}
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 12 }}>
            <Btn v="secondary" sz="sm" onClick={() => { setEditing(null); setOptimized(null); setForm({ name: '', description: '', prompt_text: '', category: 'Custom', is_global: false, inject_as_system: false }); }}>Cancel</Btn>
            <Btn v="primary" sz="sm" onClick={() => { save(); }} disabled={!form.name || !form.prompt_text}>Save</Btn>
          </div>
        </Card>
      )}
    </div>
  );
};
